import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { FEATURES } from '../../config';
import { authService } from '../../services/authService';
import { PUSH_PREFERENCE_CHANGED_EVENT } from '../../services/pushService';
import { LEARNER_SETTINGS_MODAL_CLOSED_EVENT } from '../../constants/learnerPersianFonts';
import { openLearnerSettingsToInAppAlerts } from '../../constants/learnerSettingsEvents';

export const InboxPushHint: React.FC = () => {
  const { t } = useTranslation('messages');
  const [pushOn, setPushOn] = useState<boolean | null>(null);

  useEffect(() => {
    if (!FEATURES.enablePushNotifications) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        const prefs = await authService.getLearningPreferences();
        if (!cancelled) setPushOn(Boolean(prefs.pushNotifications));
      } catch {
        if (!cancelled) setPushOn(false);
      }
    };

    void load();

    const onPreference = (event: Event) => {
      setPushOn(Boolean((event as CustomEvent<{ enabled?: boolean }>).detail?.enabled));
    };
    const onSettingsClosed = () => {
      void load();
    };

    window.addEventListener(PUSH_PREFERENCE_CHANGED_EVENT, onPreference as EventListener);
    window.addEventListener(LEARNER_SETTINGS_MODAL_CLOSED_EVENT, onSettingsClosed);
    return () => {
      cancelled = true;
      window.removeEventListener(PUSH_PREFERENCE_CHANGED_EVENT, onPreference as EventListener);
      window.removeEventListener(LEARNER_SETTINGS_MODAL_CLOSED_EVENT, onSettingsClosed);
    };
  }, []);

  const settingsLink = useMemo(
    () => (
      <button
        type="button"
        onClick={openLearnerSettingsToInAppAlerts}
        className="font-medium text-cyan-700 underline decoration-cyan-600/35 underline-offset-2 transition-colors hover:text-cyan-900 dark:text-cyan-300 dark:decoration-cyan-400/40 dark:hover:text-cyan-200"
      />
    ),
    []
  );

  if (!FEATURES.enablePushNotifications || pushOn !== false) return null;

  return (
    <div className="flex items-start gap-2 rounded-2xl border border-cyan-200/60 bg-cyan-50/50 px-3.5 py-2 text-start dark:border-cyan-800/40 dark:bg-cyan-950/25">
      <Bell className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-600/80 dark:text-cyan-400/80" aria-hidden />
      <p className="min-w-0 text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">
        <Trans
          i18nKey="inboxPushHint"
          ns="messages"
          t={t}
          defaults="Want alerts on your phone when a message arrives? You can <settingsLink>turn on notifications</settingsLink>."
          components={{
            settingsLink,
          }}
        />
      </p>
    </div>
  );
};
